import { CONVEYANCE_TYPES } from '@koala/sdk/v4';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { LoyaltyBadge } from '../checkout/account/loyalty-badge';
import { EstimatedPrepTime } from './estimated-prep-time';
import { useDispatch, useSelector } from '@/redux';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { ROUTES } from '@/constants/routes';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { toDollars } from '@/utils/basket';
import { fireGaEvent, gaCats, gaActions } from '@/utils/googleAnalytics';
import { useDialogContext } from '@/utils/useDialogContext';
import orderStatusActions from '@/redux/orderStatus/actions';
import { OrderStatuses } from '@/types/orderStatus';
import { useUtmSource } from '@/utils/hooks';
import { isAsapAvailable, isAsapValue } from '@/utils/conveyance';
import { LAYOUT } from '@/constants/styles';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { getFulfillmentTime } from '@/utils/wantedAt';
interface Props {
  isCheckoutDisabled?: boolean;
  subtotal: number;
}

export const Footer = ({ subtotal, isCheckoutDisabled }: Props) => {
  const router = useRouter();
  const { onCancel } = useDialogContext();
  const { type, address } = useSelector(selectConveyance);
  const orderStatus = useSelector((state) => state.app.orderStatus);
  const basket = useSelector((state) => state.app.basket);
  const basketLocation = useSelector((state) => state.app.basket.location);
  const basketFulfillment = useSelector((state) => state.app.basket.fulfillment);
  const dispatch = useDispatch();
  const { currentUtmSource } = useUtmSource();

  const onCheckoutClick = () => {
    if (isCheckoutDisabled) {
      return;
    }
    if (type === CONVEYANCE_TYPES.DELIVERY && !address?.street_address) {
      document.getElementById('delivery-conveyance-button')?.click();
      return;
    }
    // TEAM-14493: a basket-less PRICING_UNAVAILABLE re-initializes too. A code 13 at an init step
    // before VALIDATE (SET_WANTED_AT runs the chowly-api validate) never hydrates `checkoutBasket`,
    // so the checkout page's mount-time validate has no basket id to validate and every re-entry
    // just reopened the modal. With a basket, navigating is enough: that validate runs as usual.
    // Each press here creates a new remote basket and orphans the previous one (bounded by presses).
    const isPricingUnavailableWithoutBasket =
      orderStatus.status === OrderStatuses.PRICING_UNAVAILABLE && !basket.checkoutBasket?.id;
    if (
      orderStatus.status === OrderStatuses.ORDER_NOT_INITIALIZED ||
      isPricingUnavailableWithoutBasket
    ) {
      basket.content.utm_source =
        basket?.content && currentUtmSource ? currentUtmSource : undefined;
      const wantedAt =
        basketFulfillment?.time_wanted ?? basketFulfillment?.address?.time_wanted ?? undefined;
      dispatch(orderStatusActions.initializeOrder(basket.content, basketLocation.id, wantedAt));
    }
    fireGaEvent(gaCats.order, gaActions.clickCheckOut);
    onCancel();
    void router.push(ROUTES.CHECKOUT);
  };

  const { fulfillment, location, checkoutBasket, loading } = useSelector(
    (state) => state.app.basket,
  );
  const displaySubtotal = subtotal ?? checkoutBasket?.subtotal;
  const hasItems = subtotal > 0;
  const timeWanted = getFulfillmentTime(fulfillment);
  const fulfillmentType = fulfillment?.type || CONVEYANCE_TYPES.PICKUP;
  const isAsap = isAsapAvailable(location, fulfillmentType) && isAsapValue(timeWanted);

  return (
    <Container>
      <div>
        <LoyaltyBadge type="success" style={{ marginBottom: '10px' }} includeLinks />
        {hasItems && isAsap && <EstimatedPrepTime />}
      </div>

      <StyledTotalsRow>
        <div>Subtotal&nbsp;</div>
        <StyledOpacityLabel data-testid="subtotal-amount" opacity={loading}>
          ${toDollars(displaySubtotal)}
        </StyledOpacityLabel>
      </StyledTotalsRow>

      <Continue
        data-css-override="StyledCheckoutButton StyledPrimaryButton"
        data-testid="go-to-checkout-button"
        disabled={!!isCheckoutDisabled}
        aria-disabled={isCheckoutDisabled}
        size="large"
        onClick={() => onCheckoutClick()}
      >
        <StringAccessor
          accessor="cart_checkout.continue_to_checkout_cta"
          dataObj={{ subtotal: `$${toDollars(subtotal)}` }}
        />
      </Continue>

      <Cancel onClick={onCancel}>
        <StringAccessor accessor="cart_checkout.back_to_menu_cta" />
      </Cancel>
    </Container>
  );
};

const Container = styled.footer`
  border-block-start: 1px solid var(--sidecart__divider-color);
  display: flex;
  flex-direction: column;
  padding: var(--size-3) var(--size-6) var(--size-3);
  gap: var(--size-3);
`;

const Continue = styled(StyledPrimaryButton)<{ disabled: boolean }>`
  ${(props) =>
    props.disabled &&
    `
      opacity: 0.8;
      pointer-events: none;
    `}
`;

const Cancel = styled.button(({ theme }) => ({
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'none', // don't surface the "Back to menu" button on mobile to save space
  height: 'var(--size-10)',
  transition: 'opacity 0.2s ease',
  ...getFontStyles(theme.sidecart.font, ['color', 'font_family', 'font_style', 'line_height']),

  '&:hover': {
    opacity: 0.7,
  },

  '@media (min-width: 768px)': {
    display: 'block',
  },
}));

const StyledOpacityLabel = styled.div<{ opacity?: boolean }>`
  opacity: ${({ opacity }) => (opacity ? 0.5 : 1)};
`;

interface StyledTotalsRowProps {
  highlight?: boolean;
}

const StyledTotalsRow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'highlight' && prop !== 'isBuyBack',
})<StyledTotalsRowProps & { isBuyBack?: boolean }>`
  display: flex;
  font-size: 16px;
  margin-bottom: ${LAYOUT.GUTTER / 4}px;

  ${({ highlight, theme }) =>
    highlight &&
    `
    color: ${theme.global.primary_active_color};
  `}

  > div:first-child {
    margin-right: auto;
  }

  div {
    ${({ isBuyBack }) =>
      !isBuyBack &&
      `
      :last-child {
        text-align: right;
        flex: 1;
      }
    `}
  }
`;
